"use client";

import { useEffect, useState, useCallback, useMemo } from "react";
import { createClient } from "@/lib/supabase/client";
import type { User, Session, SupabaseClient } from "@supabase/supabase-js";
import { AUTH_REDIRECT_PATH_KEY } from "@/lib/auth/constants";
import { localePath, routes } from "@/lib/routes";
import { getSafeReturnPath } from "@/lib/auth/return-destination";
import { useAuthContactSaved } from "@/lib/auth/hooks/useAuthContactSaved";

/**
 * Callback fired when auth state changes (sign-in, sign-out, token refresh).
 * Allows the parent provider to react to auth events without owning the listener.
 */
export interface AuthStateCallbacks {
  onSignIn: (user: User) => void;
  onSignOut: () => void;
}
export interface UseAuthSessionReturn {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isAnonymous: boolean;
  supabase: SupabaseClient;
  signOut: () => Promise<void>;
  /** Manually clear session state (used by signOut in the parent) */
  clearSession: () => void;
}

/**
 * Manages Supabase auth session lifecycle:
 * - Initial session hydration (including magic-link / invite hash tokens)
 * - Auth state change listener
 * - Sign-out
 */
export function useAuthSession(callbacks: AuthStateCallbacks): UseAuthSessionReturn {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  useAuthContactSaved(user);

  // Memoize supabase client to prevent infinite re-renders
  const supabase = useMemo(() => createClient(), []);

  // Initialize auth state
  useEffect(() => {
    const initAuth = async () => {
      try {
        // Check for hash-based tokens (from magic links) and process them first
        if (typeof window !== "undefined" && window.location.hash) {
          const hashParams = new URLSearchParams(window.location.hash.substring(1));
          const accessToken = hashParams.get("access_token");
          const refreshToken = hashParams.get("refresh_token");
          const type = hashParams.get("type");
          if (accessToken && refreshToken && (type === "magiclink" || type === "invite")) {
            console.log(`[Auth] Processing ${type} tokens from URL hash`);
            const {
              data,
              error
            } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken
            });
            if (error) {
              console.error(`[Auth] Error setting session from ${type}:`, error);
            } else if (data.session) {
              console.log(`[Auth] Session established from ${type}`);

              // Check for stored redirect path (from phone login flow)
              const redirectPath = sessionStorage.getItem(AUTH_REDIRECT_PATH_KEY);
              sessionStorage.removeItem(AUTH_REDIRECT_PATH_KEY);
              if (redirectPath && redirectPath !== window.location.href) {
                console.log("[Auth] Redirecting to stored path:", redirectPath);
                window.location.href = getSafeReturnPath(redirectPath, window.location.origin);
                return;
              }

              // Invite links: redirect to organization page after session is set
              if (type === "invite") {
                window.history.replaceState(null, "", window.location.pathname);
                window.location.href = localePath(routes.organization.dashboard);
                return;
              }

              // Clear the hash from URL for cleaner UX
              window.history.replaceState(null, "", window.location.pathname + window.location.search);
            }
          }
        }
        const {
          data: {
            session: currentSession
          }
        } = await supabase.auth.getSession();
        setSession(currentSession);
        setUser(currentSession?.user ?? null);

        // Notify parent about initial session
        if (currentSession?.user) {
          callbacks.onSignIn(currentSession.user);
        }
      } catch (error) {
        console.error("Error initializing auth:", error);
      } finally {
        setIsLoading(false);
      }
    };
    initAuth();

    // Listen for auth changes
    const {
      data: {
        subscription
      }
    } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      setUser(newSession?.user ?? null);
      if (newSession?.user) {
        callbacks.onSignIn(newSession.user);
      } else {
        callbacks.onSignOut();
      }
      setIsLoading(false);
    });
    return () => {
      subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const isAnonymous = useMemo(() => user?.is_anonymous === true, [user]);
  const clearSession = useCallback(() => {
    setUser(null);
    setSession(null);
  }, []);
  const signOut = useCallback(async () => {
    try {
      await supabase.auth.signOut();
      clearSession();
    } catch (error) {
      console.error("Error signing out:", error);
    }
  }, [supabase, clearSession]);
  return {
    user,
    session,
    isLoading,
    isAnonymous,
    supabase,
    signOut,
    clearSession
  };
}